import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import {
  Announcement,
  AnnouncementEffects,
  AnnouncementFeature,
  AnnouncementFooter,
  AnnouncementIconTone,
  AnnouncementTheme,
  announcementText,
} from "../../services/announcement/announcement.models";

import { AnnouncementClientApiService } from "../../services/announcement/announcement-client-api.service";
import { AnnouncementParticlesComponent } from "./announcement-particles.component";

/** Panel colours used whenever an announcement doesn't override them. */
export const DEFAULT_ANNOUNCEMENT_THEME: Required<AnnouncementTheme> = {
  panelBg: "#171e29",
  panelText: "#ffffff",
  previewBg: "linear-gradient(135deg, #dff3f1 0%, #cfe6ff 100%)",
  accent: "#4f7cff",
  accentText: "#ffffff",
};

/** Text colours picked for a background the announcement chose. */
const LIGHT_ON_DARK = "#ffffff";
const DARK_ON_LIGHT = "#0f172a";

/**
 * Perceived brightness (0–1) of a `#rgb`, `#rrggbb` or `rgb()` colour, or null for anything
 * else — gradients and named colours are left to the author's own text colour.
 */
function brightness(color: string): number | null {
  const value = color.trim();
  let rgb: number[] | null = null;

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value);
  if (hex) {
    const digits = hex[1];
    const full =
      digits.length === 3
        ? digits
            .split("")
            .map((d) => d + d)
            .join("")
        : digits;
    rgb = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  } else {
    const parts = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(value);
    if (parts) {
      rgb = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
    }
  }

  if (!rgb || rgb.some((channel) => Number.isNaN(channel))) {
    return null;
  }
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
}

/**
 * CSS filter that flattens an icon to a white or black silhouette. Works for SVG and PNG alike,
 * unlike a colour override, so an icon can never blend into the chip behind it.
 */
function toneFilter(tone: AnnouncementIconTone | undefined | null): string | null {
  if (tone === "LIGHT") return "brightness(0) invert(1)";
  if (tone === "DARK") return "brightness(0)";
  return null;
}

/** White on a pale panel is invisible — pick the readable end of the scale instead. */
function contrastingText(background: string): string | null {
  const level = brightness(background);
  if (level === null) {
    return null;
  }
  return level > 0.6 ? DARK_ON_LIGHT : LIGHT_ON_DARK;
}

/**
 * Announcement colours merged over the defaults — blank overrides are ignored.
 *
 * The Colours step lets an admin set a panel colour without touching the text colour. That used
 * to leave the default white text on whatever they picked, so a pale banner or modal rendered as
 * an empty strip with an invisible title. A background with no matching text colour therefore
 * gets one derived from its own brightness.
 */
export function resolveAnnouncementTheme(
  theme: AnnouncementTheme | undefined | null,
): Required<AnnouncementTheme> {
  const custom = theme ?? {};
  const merged = { ...DEFAULT_ANNOUNCEMENT_THEME };
  for (const key of Object.keys(merged) as (keyof AnnouncementTheme)[]) {
    const value = custom[key];
    if (value) merged[key] = value;
  }

  if (custom.panelBg && !custom.panelText) {
    merged.panelText = contrastingText(custom.panelBg) ?? merged.panelText;
  }
  if (custom.accent && !custom.accentText) {
    merged.accentText = contrastingText(custom.accent) ?? merged.accentText;
  }
  return merged;
}

/**
 * Runtime "Discover what's new" panel — the Trello-style two-pane modal (feature list on the
 * left, live preview on the right) referenced when this feature was scoped. MODAL_SIMPLE reuses
 * the same shell with the feature list hidden (single card). TOP_BANNER is a separate, lighter
 * component (announcement-banner). The header inbox dropdown opens this same component when a
 * user re-opens an announcement they already dismissed.
 */
@Component({
  selector: "app-announcement-modal",
  standalone: true,
  imports: [CommonModule, TranslateModule, AnnouncementParticlesComponent],
  templateUrl: "./announcement-modal.component.html",
  styleUrl: "./announcement-modal.component.scss",
})
export class AnnouncementModalComponent implements OnInit, OnChanges {
  @Input({ required: true }) announcement!: Announcement;
  /** True when rendered inside the admin PC simulator — real end-user rendering, but no
   *  seen-tracking calls and no real navigation. */
  @Input() previewMode = false;
  /** Renders inside the simulator's screen (absolute) instead of over the whole page (fixed). */
  @Input() embedded = false;
  /** Language to render in — driven by the admin form's EN/FR/AR tabs while previewing. */
  @Input() initialPreviewLang = "en";
  @Output() closed = new EventEmitter<void>();

  selectedIndex = 0;
  previewLang = "en";
  readonly previewLangs = ["en", "fr", "ar"];

  constructor(
    private announcementClientApi: AnnouncementClientApiService,
    private translateService: TranslateService,
    private router: Router,
  ) {}

  get lang(): string {
    return this.previewMode ? this.previewLang : this.translateService.currentLang || "en";
  }

  setPreviewLang(lang: string): void {
    this.previewLang = lang;
  }

  get isTwoPane(): boolean {
    return this.announcement.displayStyle !== "MODAL_SIMPLE";
  }

  get features(): AnnouncementFeature[] {
    return this.announcement.features ?? [];
  }

  get selectedFeature(): AnnouncementFeature | undefined {
    return this.features[this.selectedIndex];
  }

  /**
   * The footer bar is shown when the admin switched it on. `enabled` is only treated as
   * "off" when it is explicitly false: payloads written by hand (or by an older version of
   * the form) carry the texts without the flag, and dropping the bar for those made an
   * edited footer look like it had never been saved.
   */
  get footer(): AnnouncementFooter | null {
    const footer = this.announcement.footer;
    if (!footer || footer.enabled === false) return null;
    return footer.enabled || this.hasFooterContent(footer) ? footer : null;
  }

  private hasFooterContent(footer: AnnouncementFooter): boolean {
    return !!(footer.iconValue || this.text(footer.text) || this.text(footer.note) || this.text(footer.linkLabel));
  }

  /**
   * While previewing, an enabled-but-still-empty footer would render as a bare strip and
   * read as "my footer isn't showing". Fill it with the same placeholder the form suggests.
   */
  get footerPlaceholder(): string {
    return this.previewMode ? this.translateService.instant("ANNOUNCEMENT.FOOTER.TEXT_PH") : "";
  }

  get theme(): Required<AnnouncementTheme> {
    return resolveAnnouncementTheme(this.announcement.theme);
  }

  /** Particle overlay config, or null when the admin left it off — nothing is rendered then. */
  get particleEffects(): AnnouncementEffects | null {
    const effects = this.announcement.effects;
    return effects?.enabled && effects.type ? effects : null;
  }

  /** Played inside the popup card itself. */
  get cardEffects(): AnnouncementEffects | null {
    const effects = this.particleEffects;
    return effects?.origin === "CARD" ? effects : null;
  }

  /** Played over the whole screen around the popup — the default (and nicer) placement. */
  get screenEffects(): AnnouncementEffects | null {
    const effects = this.particleEffects;
    return effects && effects.origin !== "CARD" ? effects : null;
  }

  /** Icon chip background — falls back to a translucent tint of the panel text. */
  iconBg(feature: AnnouncementFeature): string {
    return feature.iconBg || "rgba(255, 255, 255, 0.08)";
  }

  /**
   * CSS filter that flattens an icon to white or black. Fixes icons whose own colour matches
   * the chip behind them; works for SVG and PNG alike, unlike a colour override.
   */
  iconFilter(feature: AnnouncementFeature): string | null {
    return toneFilter(feature.iconTone);
  }

  /**
   * Footer colours. Each falls back to what the bar painted before it could be configured — a
   * translucent band of the panel colour with the panel's own text — so an announcement whose
   * footer was authored before these controls existed renders exactly as it did.
   */
  get footerBg(): string | null {
    return this.footer?.bg || null;
  }

  get footerText(): string | null {
    return this.footer?.textColor || this.theme.panelText;
  }

  get footerIconBg(): string | null {
    return this.footer?.iconBg || null;
  }

  get footerIconFilter(): string | null {
    return toneFilter(this.footer?.iconTone);
  }

  onFooterLinkClick(): void {
    if (this.previewMode || !this.footer?.linkUrl) return;
    window.open(this.footer.linkUrl, "_blank", "noopener,noreferrer");
  }

  text(map: Record<string, string> | undefined | null): string {
    return announcementText(map, this.lang);
  }

  ngOnInit(): void {
    // The first card is the one open on arrival; the rest collapse behind it.
    this.selectedIndex = 0;
    this.previewLang = this.previewMode ? this.initialPreviewLang : this.lang;
    this.markSeen("SEEN");
  }

  /**
   * In the simulator the announcement object is rebuilt on every keystroke and the language can
   * change from the form's tabs — keep the rendered language in sync and never leave the
   * selection pointing past the end of a shrinking feature list.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["initialPreviewLang"] && this.previewMode) {
      this.previewLang = this.initialPreviewLang;
    }
    if (changes["announcement"] && this.selectedIndex >= this.features.length) {
      this.selectedIndex = Math.max(0, this.features.length - 1);
    }
  }

  select(index: number, event?: Event): void {
    this.selectedIndex = index;
    // The panel renders on the next tick — measure after it exists so a card
    // opening near the bottom scrolls itself fully into view instead of being
    // half-cut by the list edge.
    const card = (event?.currentTarget as HTMLElement | undefined)?.closest(".anm-card");
    if (!card) return;
    setTimeout(() => card.scrollIntoView({ block: "nearest", behavior: "smooth" }), 0);
  }

  onCtaClick(feature: AnnouncementFeature): void {
    if (this.previewMode) return;
    this.markSeen("CLICKED", feature.id);
    if (feature.ctaType === "INTERNAL_ROUTE" && feature.ctaTarget) {
      this.router.navigateByUrl(feature.ctaTarget);
      this.close();
    } else if (feature.ctaType === "LINK" && feature.ctaTarget) {
      window.open(feature.ctaTarget, "_blank", "noopener,noreferrer");
    }
  }

  close(): void {
    // Inside the simulator the panel is the thing being previewed — it must stay on screen.
    if (this.embedded) return;
    this.markSeen("DISMISSED");
    this.closed.emit();
  }

  private markSeen(status: "SEEN" | "DISMISSED" | "CLICKED", featureId?: string): void {
    if (this.previewMode || !this.announcement.slug) return;
    this.announcementClientApi.markSeen(this.announcement.slug, { status, featureId: featureId ?? null }).subscribe({ error: () => undefined });
  }
}
